<template>
  <div class="accordion-item" :class="computedClasses">
    <app-button
      tag="div"
      v-b-toggle="safeId"
      :block="block"
      :variant="computedVariant"
      :size="size"
      :disabled="disabled"
      class="accordion-item-title mb-0 shadow-none"
      :class="computedTitleClasses"
    >
      <div class="d-flex justify-content-between">
        <div class="accordion-item-title-text">
          <slot name="title">{{ title }}</slot>
          <i v-if="isInvalid" class="icon icon-info"></i>
        </div>
        <i v-if="!disabled" class="icon" :class="computedIconClass"></i>
      </div>
    </app-button>
    <b-collapse
      v-if="!disabled"
      :accordion="accordion.name"
      :id="safeId"
      class="p-3 accordion-item-body"
      :visible="computedVisible"
      @show="onShow"
      @hide="onHide"
    >
      <slot></slot>
    </b-collapse>
  </div>
</template>

<script>
import AppButton from "@/components/ui/Button";

export default {
  name: "AppAccordionItem",
  props: {
    title: {
      type: String,
      required: true,
      default: null,
    },
    variant: {
      type: String,
      required: false,
      default: "primary",
    },
    block: {
      type: Boolean,
      required: false,
      default: false,
    },
    size: {
      type: String,
      required: false,
      default: "md",
    },
    state: {
      type: [String, Boolean],
      required: false,
      default: true,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  inject: ["accordion"],
  components: { AppButton },
  created() {
    this.index = this.accordion.registerItem(this);
  },
  destroyed() {
    this.accordion.unregisterItem(this);
  },
  computed: {
    safeId() {
      return `accordion-item-${Math.ceil(Math.random() * 100000000000)}`;
    },
    computedClasses() {
      return {
        "is-visible": this.computedVisible,
        [`accordion-item-size-${this.size}`]: this.size,
        "is-invalid": this.isInvalid,
        "is-valid": !this.isInvalid && this.state === "valid",
      };
    },
    computedTitleClasses() {
      return {
        [`text-${this.size}-left`]: this.size,
      };
    },
    computedIconClass() {
      return this.computedVisible ? "icon-angle-up" : "icon-angle-down";
    },
    isInvalid() {
      return this.state === false || this.state === "invalid";
    },
    computedVariant() {
      if (!this.computedVisible) {
        return this.variant;
      }

      return this.variant.replace(/outline-/, "");
    },
    computedVisible() {
      return this.visible && !this.disabled;
    },
  },
  methods: {
    onShow() {
      this.visible = true;
    },
    onHide() {
      this.visible = false;
    },
  },
  watch: {
    disabled: {
      handler(value) {
        this.visible = !value;
      },
      immediate: true,
    },
    visible: {
      handler(value) {
        if (value) {
          this.accordion.$emit("input", this.index);
          this.$emit("show", this.index);
          return;
        }

        this.$emit("hide", this.index);
      },
    },
  },
  data() {
    return {
      visible: false,
      index: null,
    };
  },
};
</script>

<style lang="scss" scoped>
.accordion-item {
  &-title {
    padding-left: $btn-padding-x / 2;
    padding-right: $btn-padding-x / 2;
    cursor: pointer;

    .icon {
      &:before {
        font-size: initial !important;
      }
    }
    &::v-deep {
      &.disabled {
        opacity: 1;
      }
    }
  }
  &.is-invalid {
    .accordion-item-title-text {
      color: $form-feedback-invalid-color !important;
    }
  }
  &.is-valid {
    .accordion-item-title-text {
      color: $form-feedback-valid-color !important;
    }
  }
}
</style>
